<template>
  <!-- Mobile Header with Hamburger (only visible on mobile) -->
  <div class="mobile-header" v-if="isMobile">
    <div class="mobile-header-content">
      <router-link to="/workspace/dashboard" class="logo-link">
        <h1 class="mobile-logo">Appointum</h1>
      </router-link>
      <button class="hamburger-button" @click="toggleMobileSidebar" :class="{ 'hamburger-open': isMobileMenuOpen }">
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>
    </div>
  </div>

  <!-- Mobile Backdrop -->
  <div v-if="isMobile && isMobileMenuOpen" class="mobile-backdrop" @click="closeMobileSidebar"></div>

  <aside class="sidebar" ref="sidebarElement" :class="{
    'sidebar-collapsed': isCollapsed && !isMobile,
    'sidebar-mobile-hidden': isMobile && !isMobileMenuOpen,
    'sidebar-mobile-open': isMobile && isMobileMenuOpen,
    'sidebar-submenu-open': isMobile && expandedMenus.includes('settings')
  }" :style="{ height: sidebarHeight + 'px' }">

    <!-- Logo and toggle (hidden on mobile) -->
    <div class="sidebar-header collapse-button-bgr" v-if="!isMobile">
      <div class="logo-container">
        <router-link to="/workspace/dashboard" class="logo-link full-logo">
          <h1 class="logo">Appointum</h1>
        </router-link>
        <router-link to="/workspace/dashboard" class="logo-link collapsed-logo">
          <div class="logo">A</div>
        </router-link>
      </div>
      <button class="collapse-button" @click="toggleSidebar">
        <PhCaretDoubleLeft v-if="!isCollapsed" />
        <PhCaretDoubleRight v-else />
      </button>
    </div>

    <!-- Mobile header inside sidebar -->
    <div class="sidebar-mobile-header" v-if="isMobile">
    </div>

    <!-- Scrollable Content Container -->
    <div class="sidebar-content">
      <!-- Main Navigation -->
      <nav class="sidebar-nav sidebar-nav-mob-gap">
        <button v-for="item in filteredMainNavItems" :key="item.id" class="nav-item"
          :class="{ active: activeSection === item.id }" :data-tooltip="isCollapsed && !isMobile ? item.label : ''"
          @click="handleNavClick(item)" :ref="item.id === 'settings' ? 'settingsButton' : null">
          <component :is="item.icon" class="nav-icon" />
          <span class="nav-label">{{ item.label }}</span>
          <PhCaretRight v-if="item.hasSubmenu && shouldShowSettingsSubmenu && (!isCollapsed || isMobile)"
            class="submenu-icon" :class="{ 'rotated': expandedMenus.includes(item.id) }" />
        </button>

        <!-- Settings Submenu (Mobile - inline under settings) -->
        <div v-if="isMobile && expandedMenus.includes('settings') && shouldShowSettingsSubmenu" class="settings-submenu-mobile-inline">
          <div v-for="(group, groupIndex) in filteredSettingsSubmenu" :key="groupIndex" class="submenu-group-mobile">
            <div class="submenu-group-title-mobile">{{ group.title }}</div>
            <button v-for="subItem in group.items" :key="subItem.id" class="nav-item submenu-item-mobile"
              @click="selectSubSection('settings', subItem.id)">
              <span class="submenu-label">{{ subItem.label }}</span>
            </button>
          </div>
        </div>
      </nav>

      <!-- Onboarding Banner -->
      <div v-if="showOnboardingBanner" class="onboarding-section">
        <!-- Full banner for expanded sidebar or mobile -->
        <div v-if="!isCollapsed || isMobile" class="onboarding-banner">
          <div class="banner-header">
            <div class="banner-title-section">
              <span class="banner-title">Pabeigt iestatīšanu</span>
            </div>
            <button class="banner-close-btn" @click="showDismissOnboardingModal = true">
              <PhX />
            </button>
          </div>

          <div class="progress-section">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: completionPercentage + '%' }"></div>
            </div>
            <div class="progress-text">
              {{ completionPercentage }}% pabeigts
            </div>
          </div>

          <div class="banner-footer">
            <button class="primary-btn continue-btn" @click="continueOnboarding">
              <span>Turpināt</span>
            </button>
          </div>
        </div>

        <!-- Collapsed banner for collapsed sidebar (desktop only) -->
        <div v-else class="onboarding-banner-collapsed">
          <div class="collapsed-content" @click="continueOnboarding">
            <div class="progress-ring">
              <svg class="progress-ring-svg" width="36" height="36" viewBox="0 0 28 28">
                <circle cx="14" cy="14" r="12" fill="none" stroke="#e5e7eb" stroke-width="3" />
                <circle cx="14" cy="14" r="12" fill="none" stroke="#036D19" stroke-width="3"
                  :stroke-dasharray="2 * Math.PI * 12"
                  :stroke-dashoffset="2 * Math.PI * 12 * (1 - completionPercentage / 100)" stroke-linecap="round"
                  class="progress-circle" />
              </svg>
              <div class="progress-percentage">{{ completionPercentage }}%</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Secondary Navigation (Help and Report) -->
      <nav class="sidebar-nav secondary-nav">
        <button v-for="item in filteredSecondaryNavItems" :key="item.id" class="nav-item"
          :class="{ active: activeSection === item.id }" :data-tooltip="isCollapsed && !isMobile ? item.label : ''"
          @click="selectSection(item.id)">
          <component :is="item.icon" class="nav-icon" />
          <span class="nav-label">{{ item.label }}</span>
        </button>
      </nav>
    </div>

    <!-- Settings Submenu (Desktop - Right-aligned) -->
    <div v-if="!isMobile && expandedMenus.includes('settings') && shouldShowSettingsSubmenu"
      ref="settingsSubmenu"
      class="settings-submenu"
      :class="{
        'settings-submenu-collapsed': isCollapsed
      }"
      :style="{ top: submenuTop + 'px' }">
      
      <div v-for="(group, groupIndex) in filteredSettingsSubmenu" :key="groupIndex" class="submenu-group">
        <div class="submenu-group-title">{{ group.title }}</div>
        <button v-for="subItem in group.items" :key="subItem.id" class="nav-item submenu-item"
          @click="selectSubSection('settings', subItem.id)">
          <span class="submenu-label">{{ subItem.label }}</span>
        </button>
      </div>
    </div>

    <!-- User Profile Section -->
    <div class="sidebar-footer">
      <div class="user-profile" :class="{ 'profile-active': activeSection === 'account-settings' }"
        @click="toggleProfileMenu">
        <ProfileIcon :username=getUserPreferredName />
        <div class="user-info" v-if="!isCollapsed || isMobile">
          <div class="user-name">{{ getUserPreferredName }}</div>
        </div>
        <PhCaretRight class="profile-caret-icon" :class="{ 'rotated': isProfileMenuOpen }"
          v-if="!isCollapsed || isMobile" />
      </div>

      <!-- Profile Dropdown Menu (Mobile - inline under profile) -->
      <div v-if="isMobile && isProfileMenuOpen" class="profile-dropdown-mobile-inline">
        <button class="profile-dropdown-item" @click.stop="handleAccountSettings">
          Konta iestatījumi
        </button>
        <button class="profile-dropdown-item" @click.stop="handleLogout">
          Izlogoties
        </button>
      </div>

      <!-- Profile Dropdown Menu (Desktop - floating) -->
      <div v-if="!isMobile && isProfileMenuOpen" class="profile-dropdown" :class="{
        'profile-dropdown-collapsed': isCollapsed
      }">
        <button class="profile-dropdown-item" @click.stop="handleAccountSettings">
          Konta iestatījumi
        </button>
        <button class="profile-dropdown-item" @click.stop="handleLogout">
          Izlogoties
        </button>
      </div>
    </div>

    <!-- Dismiss Onboarding Confirmation Modal -->
    <BaseModal
      v-model="showDismissOnboardingModal"
      title="Pabeigt iestatīšanu"
      :buttons="dismissOnboardingButtons"
      :width="400"
    >
      <div class="confirmation-content">
        <p>Vai tiešām vēlaties uzstādīt iestatīšanu kā pabeigtu? Jūs vairs nevarēsiet atgriezties pie ceļveža.</p>
      </div>
    </BaseModal>
  </aside>
</template>

<script>
import {
  PhHouse,
  PhCalendarBlank,
  PhList,
  PhSuitcaseSimple,
  PhUserPlus,
  PhUsers,
  PhGearSix,
  PhCaretDoubleLeft,
  PhCaretDoubleRight,
  PhCaretRight,
  PhQuestion,
  PhFlag,
  PhX,
  PhCheckCircle,
  PhCircle,
  PhCreditCard
} from '@phosphor-icons/vue'

import ProfileIcon from './ProfileIcon.vue';
import { useAuthStore } from '../stores/auth';
import { useFeaturesStore } from '../stores/features';
import authService from '../services/authService';
import { markRaw } from 'vue';

export default {
  name: 'SidebarNavigation',
  emits: ['toggle-sidebar', 'section-changed'],
  components: {
    PhHouse,
    PhCalendarBlank,
    PhList,
    PhSuitcaseSimple,
    PhUserPlus,
    PhUsers,
    PhGearSix,
    PhCaretDoubleLeft,
    PhCaretDoubleRight,
    PhCaretRight,
    PhQuestion,
    PhFlag,
    PhX,
    CheckCircle: PhCheckCircle,
    Circle: PhCircle,
    ProfileIcon,
    PhCreditCard
  },
  setup() {
    const auth = useAuthStore()
    const featuresStore = useFeaturesStore()
    return { auth, featuresStore }
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false
    },
    currentSection: {
      type: String,
      default: 'dashboard'
    }
  },
  data() {
    return {
      isCollapsed: false,
      isMobile: false,
      isMobileMenuOpen: false,
      activeSection: '',
      activeSubSection: '',
      expandedMenus: [],
      isProfileMenuOpen: false,
      onboardingProgress: {},
      onboardingLoaded: false,
      showDismissOnboardingModal: false,
      submenuTop: 0,
      mainNavItems: [
        { id: 'dashboard', label: 'Kontrolpanelis', icon: markRaw(PhHouse) },
        { id: 'calendar', label: 'Kalendārs', icon: markRaw(PhCalendarBlank) },
        { id: 'appointments', label: 'Pieraksti', icon: markRaw(PhList) },
        { id: 'clients', label: 'Klienti', icon: markRaw(PhUsers) },
        { id: 'services', label: 'Pakalpojumi', icon: markRaw(PhSuitcaseSimple) },
        { id: 'providers', label: 'Speciālisti', icon: markRaw(PhUserPlus) },
        {
          id: 'settings',
          label: 'Iestatījumi',
          icon: markRaw(PhGearSix),
          hasSubmenu: true
        }
      ],
      secondaryNavItems: [
        //{ id: 'help', label: 'Palīdzība', icon: markRaw(PhQuestion) },
        { id: 'report', label: 'Ziņot', icon: markRaw(PhFlag) }
      ],
      settingsSubmenu: [
        {
          type: 'group',
          title: 'Uzņēmums',
          items: [
            { id: 'info', label: 'Pamatinformācija' },
            { id: 'working-hours', label: 'Darba laiki' }
          ]
        },
        {
          type: 'group',
          title: 'Pieraksti',
          items: [
            { id: 'booking-form', label: 'Pierakstu forma' },
            { id: 'rules', label: 'Nosacījumi' }
          ]
        },
        {
          type: 'group',
          title: 'Appointum',
          items: [
            { id: 'features', label: 'Funkcijas' }
          ]
        },
        {
          type: 'group',
          title: 'Cits',
          items: [
            { id: 'team', label: 'Komanda' },
            { id: 'tags', label: 'Tagi' }
          ]
        }
      ],
      sidebarHeight: window.innerHeight
    };
  },
  computed: {
    getUserPreferredName() {
      return this.auth.getPreferredName
    },
    filteredMainNavItems() {
      let items = this.mainNavItems.filter(item => {
        return this.auth.canAccessSection(item.id)
      })

      // Add subscriptions item if the feature is enabled
      if (this.featuresStore.isEnabled('subscriptions')) {
        const clientsIndex = items.findIndex(item => item.id === 'clients')
        if (clientsIndex !== -1) {
          items.splice(clientsIndex + 1, 0, {
            id: 'subscriptions',
            label: 'Abonementi',
            icon: markRaw(PhCreditCard)
          })
        }
      }

      return items
    },
    filteredSecondaryNavItems() {
      return this.secondaryNavItems.filter(item => {
        return this.auth.canAccessSection(item.id)
      })
    },
    filteredSettingsSubmenu() {
      return this.settingsSubmenu.map(group => ({
        ...group,
        items: group.items.filter(subItem =>
          this.auth.canAccessSubSection('settings', subItem.id)
        )
      })).filter(group => group.items.length > 0)
    },
    shouldShowSettingsSubmenu() {
      return this.auth.canAccessSection('settings') && this.filteredSettingsSubmenu.length > 0
    },
    showOnboardingBanner() {
      return this.onboardingLoaded && !this.onboardingProgress.isCompleted && !this.onboardingProgress.isDismissed;
    },
    dismissOnboardingButtons() {
      return [
        {
          title: 'Atcelt',
          position: 'right',
          class: 'secondary-btn',
          onClick: () => { this.showDismissOnboardingModal = false; }
        },
        {
          title: 'Pabeigt',
          position: 'right',
          class: 'primary-btn',
          onClick: this.dismissOnboarding
        }
      ];
    },
    orderedSteps() {
      if (!this.onboardingProgress || Object.keys(this.onboardingProgress).length === 0) {
        return [];
      }

      return Object.entries(this.onboardingProgress)
        .filter(([, value]) => value && typeof value === 'object' && value.index)
        .map(([key, value]) => ({
          key,
          ...value
        }))
        .sort((a, b) => a.index - b.index);
    },
    completionPercentage() {
      if (this.onboardingProgress && this.onboardingProgress.completionPercentage !== undefined) {
        return this.onboardingProgress.completionPercentage;
      }

      const steps = this.orderedSteps;
      if (steps.length === 0) return 0;

      const completedSteps = steps.filter(step => step.completed).length;
      return Math.round((completedSteps / steps.length) * 100);
    }
  },
  watch: {
    currentSection(newSection) {
      if (newSection) {
        this.activeSection = newSection;
      }
    },
    '$route': {
      handler(to, from) {
        if (to.path !== from?.path) {
          this.updateActiveStateFromRoute(to);
        }
      },
      immediate: true
    },
    isMobileMenuOpen(isOpen) {
      // Lock body scroll when mobile menu is open
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },
  async mounted() {
    this.checkMobile();

    if (window.innerWidth >= 769 && window.innerWidth < 1200) {
      this.isCollapsed = true;
    } else {
      this.isCollapsed = this.getInitialCollapsedState();
    }

    await this.loadOnboardingProgress();
    await this.featuresStore.loadFeatures();
    this.featuresStore.initMultiTabSync();

    this.updateActiveStateFromRoute();

    if (!this.activeSection && this.currentSection) {
      this.activeSection = this.currentSection;
    }

    this.updateHeight()
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('click', this.handleClickOutside)
    this.featuresStore.cleanup()
    // Ensure body scroll is restored
    document.body.style.overflow = '';
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 769;

      // Auto-collapse sidebar for screens between 769px and 1199px
      const shouldAutoCollapse = window.innerWidth >= 769 && window.innerWidth < 1200;

      if (this.isMobile) {
        this.isMobileMenuOpen = false;
      } else if (shouldAutoCollapse) {
        // Auto-collapse if not already collapsed
        this.isCollapsed = true;
        this.$emit('toggle-sidebar', this.isCollapsed);
      }
    },
    toggleMobileSidebar() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    closeMobileSidebar() {
      this.isMobileMenuOpen = false;
      this.expandedMenus = [];
      this.isProfileMenuOpen = false;
    },
    async loadOnboardingProgress() {
      try {
        const response = await this.$http.get('/api/onboarding/progress/');
        if (response.data?.code === 'ok') {
          this.onboardingProgress = response.data.data;
        }
      } catch (error) {
        console.error('Error loading onboarding progress:', error);
        // Set default state on error to prevent showing banner
        this.onboardingProgress = { isCompleted: true };
      } finally {
        // Set loaded to true regardless of success or failure
        this.onboardingLoaded = true;
      }
    },
    continueOnboarding() {
      this.$router.push('/onboarding');
      if (this.isMobile) {
        this.closeMobileSidebar();
      }
    },
    async dismissOnboarding() {
      try {
        const response = await this.$http.post('/api/onboarding/dismiss/');
        if (response.data?.code === 'ok') {
          this.onboardingProgress.isDismissed = true;
          this.showDismissOnboardingModal = false;
        }
      } catch (error) {
        console.error('Error dismissing onboarding:', error);
      }
    },
    getInitialCollapsedState() {
      if (this.collapsed !== false) {
        return this.collapsed;
      }
      try {
        const saved = localStorage.getItem('sidebar-collapsed');
        return saved !== null ? JSON.parse(saved) : false;
      } catch (error) {
        console.warn('Failed to load sidebar state from localStorage:', error);
        return false;
      }
    },
    saveSidebarState(collapsed) {
      try {
        localStorage.setItem('sidebar-collapsed', JSON.stringify(collapsed));
      } catch (error) {
        console.warn('Failed to save sidebar state to localStorage:', error);
      }
    },
    toggleSidebar() {
      if (this.isMobile) {
        this.toggleMobileSidebar();
        return;
      }

      this.isCollapsed = !this.isCollapsed;
      this.saveSidebarState(this.isCollapsed);
      this.$emit('toggle-sidebar', this.isCollapsed);
      this.updateSubmenuPosition();
    },
    handleNavClick(item) {
      if (!this.auth.canAccessSection(item.id)) {
        console.warn(`Jums nav pieejas sadaļai: ${item.id}`);
        return;
      }

      if (item.hasSubmenu) {
        this.toggleSubmenu(item.id);
      } else {
        this.selectSection(item.id);
      }
    },
    toggleSubmenu(menuId) {
      if (this.expandedMenus.includes(menuId)) {
        this.expandedMenus = this.expandedMenus.filter(id => id !== menuId);
      } else {
        this.expandedMenus = [menuId]; // Ensure only one submenu open at a time
        this.isProfileMenuOpen = false; // Close profile menu when submenu opens
        if (!this.isMobile && menuId === 'settings') {
          // Use setTimeout to ensure DOM is fully rendered
          setTimeout(() => {
            this.updateSubmenuPosition();
          }, 10);
        }
      }
    },
    updateSubmenuPosition() {
      if (this.expandedMenus.includes('settings') && this.$refs.settingsButton && this.$refs.sidebarElement) {
        const button = this.$refs.settingsButton[0];
        const sidebar = this.$refs.sidebarElement;

        const rect = button.getBoundingClientRect();
        const asideRect = sidebar.getBoundingClientRect();

        // Default position: align with the settings button
        let calculatedTop = rect.top - asideRect.top;

        // Use nextTick to measure the actual submenu after it's rendered
        this.$nextTick(() => {
          const submenuEl = this.$refs.settingsSubmenu;
          if (submenuEl) {
            const submenuHeight = submenuEl.offsetHeight;
            const submenuBottom = rect.top + submenuHeight;
            const viewportHeight = window.innerHeight;
            const margin = 16; // Minimum margin from viewport edge

            // If submenu would overflow the viewport, shift it up
            if (submenuBottom > viewportHeight - margin) {
              const overflow = submenuBottom - (viewportHeight - margin);
              calculatedTop = Math.max(margin, calculatedTop - overflow);
            }

            this.submenuTop = calculatedTop;
          } else {
            this.submenuTop = calculatedTop;
          }
        });
      }
    },
    selectSection(sectionId) {
      this.activeSection = sectionId;
      this.activeSubSection = '';
      this.expandedMenus = []; // Close submenu when selecting a section
      this.isProfileMenuOpen = false; // Close profile menu

      // Close mobile menu when selecting a section on mobile
      if (this.isMobile) {
        this.closeMobileSidebar();
      }

      this.$emit('section-changed', sectionId);
    },
    selectSubSection(parentId, subSectionId) {
      if (!this.auth.canAccessSubSection(parentId, subSectionId)) {
        console.warn(`Jums nav pieejas sadaļai: ${parentId}/${subSectionId}`);
        return;
      }

      this.activeSection = parentId;
      this.activeSubSection = subSectionId;
      this.expandedMenus = [];
      this.isProfileMenuOpen = false;

      // Close mobile menu when selecting a subsection on mobile
      if (this.isMobile) {
        this.closeMobileSidebar();
      }

      this.$emit('section-changed', parentId, subSectionId);
    },
    toggleProfileMenu() {
      this.isProfileMenuOpen = !this.isProfileMenuOpen;
      if (this.isProfileMenuOpen) {
        this.expandedMenus = []; // Close submenu when profile menu opens
      }
    },
    handleClickOutside(event) {
      if (!event.target.closest('.user-profile') && !event.target.closest('.profile-dropdown') &&
        !event.target.closest('.nav-item') && !event.target.closest('.settings-submenu') &&
        !event.target.closest('.hamburger-button')) {
        this.isProfileMenuOpen = false;
        this.expandedMenus = [];

        // Close mobile menu if clicking outside on mobile
        if (this.isMobile && !event.target.closest('.sidebar') && !event.target.closest('.mobile-header')) {
          this.closeMobileSidebar();
        }
      }
    },
    handleAccountSettings() {
      this.isProfileMenuOpen = false;
      this.expandedMenus = [];

      this.activeSection = 'account-settings';
      this.activeSubSection = '';

      // Close mobile menu on mobile
      if (this.isMobile) {
        this.closeMobileSidebar();
      }

      this.$emit('section-changed', 'account-settings');
    },
    handleLogout() {
      this.isProfileMenuOpen = false;
      this.expandedMenus = [];

      // Close mobile menu on mobile
      if (this.isMobile) {
        this.closeMobileSidebar();
      }

      this.auth.clearUser();
      authService.logout();
      this.$router.push('/');
    },
    updateHeight() {
      this.sidebarHeight = window.innerHeight;
    },
    handleResize() {
      const wasMobile = this.isMobile;
      this.checkMobile();

      // If switching from mobile to desktop, close mobile menu
      if (wasMobile && !this.isMobile) {
        this.isMobileMenuOpen = false;
      }

      this.updateHeight();
    },
    updateActiveStateFromRoute(route = null) {
      const currentRoute = route || this.$route;
      if (!currentRoute) return;

      const pathSegments = currentRoute.path.split('/').filter(segment => segment !== '');
      const workspaceIndex = pathSegments.indexOf('workspace');

      if (workspaceIndex !== -1 && workspaceIndex < pathSegments.length - 1) {
        const sectionAfterWorkspace = pathSegments[workspaceIndex + 1];

        if (sectionAfterWorkspace === 'account-settings') {
          this.activeSection = 'account-settings';
          this.activeSubSection = '';
          return;
        }

        if (sectionAfterWorkspace === 'settings') {
          this.activeSection = 'settings';

          if (workspaceIndex + 2 < pathSegments.length) {
            const subSection = pathSegments[workspaceIndex + 2];
            const subMenuItem = this.settingsSubmenu.find(item => item.id === subSection);

            if (subMenuItem) {
              this.activeSubSection = subSection;
              // Don't automatically open submenu - just highlight settings
            } else {
              this.activeSubSection = '';
            }
          } else {
            this.activeSubSection = '';
          }
          return;
        }

        const mainNavItem = this.mainNavItems.find(item => item.id === sectionAfterWorkspace);
        const secondaryNavItem = this.secondaryNavItems.find(item => item.id === sectionAfterWorkspace);
        const filteredMainNavItem = this.filteredMainNavItems.find(item => item.id === sectionAfterWorkspace);
        if (mainNavItem || secondaryNavItem || filteredMainNavItem) {
          this.activeSection = sectionAfterWorkspace;
          this.activeSubSection = '';
          return;
        }

        const subMenuItem = this.settingsSubmenu.find(item => item.id === sectionAfterWorkspace);
        if (subMenuItem) {
          this.activeSection = 'settings';
          this.activeSubSection = sectionAfterWorkspace;
          // Don't automatically open submenu - just highlight settings
          return;
        }
      }

      if (!this.activeSection) {
        this.activeSection = 'dashboard';
        this.activeSubSection = '';
      }
    },
  }
};
</script>

<style scoped>
/* Mobile Header Styles */
.mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 48px;
  background-color: transparent;
  z-index: 1002;
  display: none;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.mobile-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 100%;
}

.mobile-header-content .logo-link {
  position: static;
  transform: none;
}

.mobile-logo {
  font-size: 18px;
  font-weight: 500;
  color: var(--text-color-primary);
  margin: 0;
}

.hamburger-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 32px;
  height: 32px;
  position: relative;
}

.hamburger-line {
  width: 20px;
  height: 1px;
  background-color: var(--text-color-primary);
  transition: all 0.3s ease;
  transform-origin: center;
}

.hamburger-button.hamburger-open .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(4px, 4px);
}

.hamburger-button.hamburger-open .hamburger-line:nth-child(2) {
  opacity: 0;
}

.hamburger-button.hamburger-open .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(4px, -4px);
}

/* Mobile Backdrop */
.mobile-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

/* Base Sidebar Styles */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 220px;
  background-color: var(--background-color);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  z-index: 100;
  overflow: visible;
}

.sidebar-collapsed {
  width: 70px;
}

/* Mobile Sidebar States */
.sidebar-mobile-hidden {
  transform: translateX(-100%);
  z-index: 1000;
}

.sidebar-mobile-open {
  transform: translateX(0);
  z-index: 1000;
  width: 100vw;
  box-shadow: none;
}

/* Mobile Header inside Sidebar */
.sidebar-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  height: 10px;
  flex-shrink: 0;
  /* Grows this spacer by the same amount the real fixed .mobile-header
     grows by in the installed iOS PWA, so the nav items below it keep
     clearing the header instead of sliding up underneath it. */
  padding-top: calc(16px + env(safe-area-inset-top, 0px));
}

.mobile-close-button {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-color-primary);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.mobile-close-button:hover {
  background-color: var(--text-color-primary);
}

.mobile-close-button svg {
  width: 20px;
  height: 20px;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 44px 16px 16px 16px;
  height: 64px;
  position: relative;
  flex-shrink: 0;
  overflow: visible;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: visible;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Custom scrollbar styling */
.sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

/* For Firefox */
.sidebar-content {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}

.logo-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.logo {
  font-size: 24px;
  font-weight: 500;
  color: var(--text-color-primary);
  margin: 0;
  transition: all 0.3s ease;
}

.logo-link {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-decoration: none;
}

.logo-link.full-logo {
  white-space: nowrap;
  opacity: 1;
  visibility: visible;
}

.logo-link.collapsed-logo {
  opacity: 0;
  visibility: hidden;
}

.logo-link.collapsed-logo .logo {
  font-size: 28px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
}

.sidebar-collapsed .logo-link.full-logo {
  opacity: 0;
  visibility: hidden;
}

.sidebar-collapsed .logo-link.collapsed-logo {
  opacity: 1;
  visibility: visible;
}

.collapse-button {
  position: absolute;
  right: -21px;
  top: 78px;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--text-color-primary);
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  z-index: 1001;
}

.collapse-button-bgr::after {
  content: '';
  position: absolute;
  right: -1px;
  top: 78px;
  transform: translateY(-50%);
  width: 2px;
  height: 40px;
  background-color: var(--background-color);
  z-index: 99;
}

.collapse-button:hover {
  background-color: transparent;
  color: var(--text-color-primary);
  transform: translateY(-50%) scale(1.1);
}

.collapse-button svg {
  width: 20px;
  height: 20px;
}

/* Navigation Styles */
.sidebar-nav {
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  overflow: visible;
}

.secondary-nav {
  margin-top: auto;
  padding-top: 16px;
  padding-bottom: 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 16px 16px 16px 18px;
  border-radius: 50px;
  cursor: pointer;
  border: none;
  background-color: transparent;
  color: var(--text-color-primary2);
  text-align: left;
  width: 100%;
  transition: all 0.2s ease;
  position: relative;
  overflow: visible;
}

.nav-item.active {
  background-color: var(--selected-item-background);
}

/* Only apply hover on devices that support hover (not touch) */
@media (hover: hover) and (pointer: fine) {
  .nav-item:hover {
    background-color: var(--selected-item-background);
  }
}

.nav-icon {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  flex-shrink: 0;
}

.sidebar-collapsed .nav-icon {
  margin-right: 0;
}

.nav-label {
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  flex: 1;
  transition: opacity 0.3s ease;
}

.sidebar-collapsed .nav-label {
  opacity: 0;
  width: 0;
}

.submenu-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}

.submenu-icon.rotated {
  transform: rotate(180deg);
}

/* Settings Submenu Styles */
.settings-submenu {
  position: absolute;
  left: 225px;
  background-color: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 16px 8px;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  transition: opacity 0.2s ease, transform 0.2s ease;
  transform: translateY(0);
}

.settings-submenu-collapsed {
  left: 75px;
}

/* Mobile inline settings submenu */
.settings-submenu-mobile-inline {
  background-color: rgba(0, 0, 0, 0.02);
  margin: 8px 12px 0 12px;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
}

.submenu-item-mobile {
  display: flex;
  align-items: center;
  padding: 16px 16px 16px 18px;
  border-radius: 50px;
  cursor: pointer;
  border: none;
  background-color: transparent;
  color: var(--text-color-primary2);
  text-align: left;
  font-size: 15px;
  transition: all 0.2s ease;
  margin: 2px 0;
  flex-shrink: 0;
  min-width: 0;
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 12px 18px !important;
  border-radius: 50px;
  cursor: pointer;
  border: none;
  background-color: transparent;
  color: var(--text-color-primary2);
  text-align: left;
  width: 100%;
  font-size: 15px;
  transition: all 0.2s ease;
}

/* Only apply hover on devices that support hover (not touch) */
@media (hover: hover) and (pointer: fine) {
  .submenu-item:hover {
    background-color: var(--selected-item-background);
  }
}

.submenu-item.active {
  background-color: var(--selected-item-background);
}

.submenu-label {
  font-weight: 500;
  white-space: nowrap;
}

/* Onboarding Banner Styles */
.onboarding-section {
  margin: 0 12px;
  flex-shrink: 0;
  max-width: 260px;
}

.onboarding-banner {
  padding: 12px;
  background: var(--background-table);
  border-radius: 12px;
  margin: 12px 0;
  font-size: 14px;
}

.banner-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.banner-title-section {
  display: flex;
  align-items: center;
  gap: 6px;
}

.banner-title {
  font-weight: 500;
  color: var(--text-color-primary);
  font-size: 14px;
}

.banner-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color-secondary);
  border-radius: 4px;
  transition: all 0.2s ease;
}

.banner-close-btn:hover {
  color: var(--text-color-primary);
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
}

.banner-close-btn svg {
  width: 16px;
  height: 16px;
}

.progress-section {
  margin-bottom: 16px;
}

.progress-bar {
  height: 3px;
  background-color: var(--background-color);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}

.progress-fill {
  height: 100%;
  background: #036D19;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: var(--text-color-secondary);
  text-align: center;
}

.banner-footer {
  text-align: center;
}

.continue-btn {
  padding: 6px 8px;
  font-size: 11px;
  display: flex;
  gap: 4px;
  width: 100%;
}

.collapsed-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50px;
  transition: background-color 0.2s ease;
}

.collapsed-content:hover {
  background-color: var(--selected-item-background);
}

.progress-ring {
  position: relative;
  display: inline-block;
}

.progress-ring-svg {
  transform: rotate(-90deg);
}

.progress-circle {
  transition: stroke-dashoffset 0.3s ease;
}

.progress-percentage {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 9px;
  font-weight: 600;
  color: #036D19;
}

/* Footer Styles */
.sidebar-footer {
  padding: 16px;
  flex-shrink: 0;
  background-color: var(--background-color);
  position: relative;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 50px;
  padding: 16px 20px 16px 18px;
  margin: -8px;
  position: relative;
  color: var(--text-color-primary2);
}

.sidebar-collapsed .user-profile {
  justify-content: center;
  padding: 12px;
  margin: -8px -4px;
  gap: 0;
}

.sidebar-collapsed .profile-menu-icon {
  display: none;
}

/* Only apply hover on devices that support hover (not touch) */
@media (hover: hover) and (pointer: fine) {
  .user-profile:hover {
    background-color: var(--selected-item-background);
  }
}

.user-info {
  overflow: hidden;
  transition: opacity 0.3s ease;
  flex: 1;
}

.sidebar-collapsed .user-info {
  opacity: 0;
  width: 0;
}

.sidebar-collapsed .nav-item {
  padding: 12px;
  border-radius: 50px;
}

.sidebar-collapsed .nav-item .nav-icon {
  margin-right: 0;
  width: 20px;
  height: 20px;
}

.user-name {
  color: var(--text-color-primary2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s ease;
}

.profile-caret-icon {
  width: 16px;
  height: 16px;
  color: var(--text-color-secondary);
  transition: transform 0.2s ease;
}

.profile-caret-icon.rotated {
  transform: rotate(180deg);
}

/* Profile Dropdown Styles */
.profile-dropdown {
  position: absolute;
  left: 225px;
  bottom: 16px;
  background-color: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 16px 8px;
  z-index: 1000;
  white-space: nowrap;
  transition: opacity 0.2s ease, transform 0.2s ease;
  transform: translateY(0);
}

.profile-dropdown-collapsed {
  left: 75px;
}

.profile-dropdown-mobile {
  position: static;
  left: auto;
  bottom: auto;
  border: none;
  box-shadow: none;
  background-color: rgba(0, 0, 0, 0.02);
  margin: 8px 0 0 0;
  border-radius: 8px;
}

.profile-dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: 15px;
  font-weight: 500;
  color: var(--tetx-color-primary2);
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 50px;
  margin: 0 4px;
}

/* Only apply hover on devices that support hover (not touch) */
@media (hover: hover) and (pointer: fine) {
  .profile-dropdown-item:hover {
    background-color: var(--selected-item-background);
  }
}

/* Mobile profile dropdown items */
.profile-dropdown-mobile-inline .profile-dropdown-item {
  margin: 2px 8px;
  padding: 12px 24px;
}

.profile-dropdown-mobile-inline {
  margin-top: 20px;
}

.user-profile.profile-active {
  background-color: var(--selected-item-background);
}

/* Submenu Group Styles */
.submenu-group {
  margin-bottom: 12px;
}

.submenu-group:last-child {
  margin-bottom: 0;
}

.submenu-group-title {
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 1.2px;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  padding: 8px 18px 4px 18px;
  margin-bottom: 2px;
}

.submenu-group-mobile {
  margin-bottom: 16px;
}

.submenu-group-mobile:last-child {
  margin-bottom: 0;
}

.submenu-group-title-mobile {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 18px 4px 18px;
  margin-bottom: 2px;
}

/* Add spacing between groups */
.submenu-group + .submenu-group {
  padding-top: 8px;
  border-top: 1px solid var(--border-color);
}

.submenu-group-mobile + .submenu-group-mobile {
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* When settings submenu is open on mobile, make sidebar scrollable and footer flow with content */
.sidebar-submenu-open {
  overflow-y: auto;
}

.sidebar-submenu-open .sidebar-content {
  flex: none;
  overflow: visible;
}

.sidebar-submenu-open .sidebar-footer {
  flex-shrink: 0;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {

  /* Show mobile header */
  .mobile-header {
    display: block;
  }

  /* Add top margin to main content to account for mobile header */
  body {
    padding-top: 64px;
  }

  /* Hide desktop sidebar header */
  .sidebar-header {
    display: none;
  }

  /* Sidebar mobile positioning */
  .sidebar {
    top: 0;
    height: 100vh !important;
    height: 100dvh !important;
  }

  .sidebar-nav-mob-gap {
    margin-top: 24px;
  }

  /* Reset collapsed styles on mobile */
  .sidebar-mobile-open .nav-item {
    width: auto;
    height: auto;
    padding: 16px 16px 16px 18px;
    border-radius: 50px;
    justify-content: flex-start;
  }

  .sidebar-mobile-open .nav-icon {
    margin-right: 12px;
    width: 20px;
    height: 20px;
  }

  .sidebar-mobile-open .nav-label {
    opacity: 1;
    width: auto;
  }

  .sidebar-mobile-open .user-info {
    opacity: 1;
    width: auto;
  }

  .sidebar-mobile-open .profile-menu-icon {
    display: block;
  }

  /* Ensure mobile backdrop doesn't interfere with clicks */
  .mobile-backdrop {
    pointer-events: auto;
  }
}

/* Desktop Styles */
@media (min-width: 769px) {
  .mobile-header {
    display: none !important;
  }

  .sidebar-mobile-header {
    display: none;
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    transform: none !important;
  }

  .mobile-backdrop {
    display: none;
  }

  /* Tooltip for collapsed state on desktop */
  .sidebar-collapsed .nav-item[data-tooltip]:not([data-tooltip=""]):hover::after {
    content: attr(data-tooltip);
    position: fixed;
    left: 75px;
    transform: translateY(-50%);
    background-color: var(--background-tooltip);
    color: white;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 9999;
    margin-left: 4px;
    margin-top: 24px;
    opacity: 0;
    animation: fadeInTooltip 0.2s ease-in-out forwards;
    pointer-events: none;
  }

  @keyframes fadeInTooltip {
    to {
      opacity: 1;
    }
  }
}

@media (max-width: 480px) {
  .mobile-header {
    height: 36px;
  }

  .mobile-logo {
    font-size: 16px;
    font-weight: 400;
    color: var(--text-color-primary)
  }

  .hamburger-button {
    width: 28px;
    height: 28px;
  }

  .hamburger-line {
    height: 1px;
  }

  .continue-btn {
    padding: 10px 12px;
    font-size: 14px;
  }

  .onboarding-section {
    margin-left: auto;
    margin-right: auto;
    min-width: 260px;
  }
}

/* Improved animations and transitions */
.sidebar {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-mobile-open {
  animation: slideInFromLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-mobile-hidden {
  animation: slideOutToLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideInFromLeft {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
}

@keyframes slideOutToLeft {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-100%);
  }
}

/* Ensure proper z-index stacking */
.mobile-backdrop {
  z-index: 999;
}

.sidebar-mobile-open {
  z-index: 1000;
}

.settings-submenu,
.profile-dropdown {
  z-index: 99999;
}

@supports (height: 100dvh) {
  .sidebar {
    height: 100dvh; 
  }
}

/* Fallback for older browsers + extra padding for mobile */
@media (max-width: 768px) {
  .sidebar {
    height: 100vh;
    /* Subtract estimated browser UI height */
    height: calc(100vh - 60px);
  }

  .sidebar-footer {
    /* Add padding to push content above browser navigation */
    padding-bottom: calc(16px + 10px);
  }

  /* For devices with safe area (newer phones) */
  .sidebar-footer {
    padding-bottom: calc(16px + 10px + env(safe-area-inset-bottom, 0px));
  }
}
</style>